import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import * as XLSX from "xlsx";
import {
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Download,
  Filter,
  LocateFixed,
  Loader2,
  MapPin,
  MapPinned,
  RefreshCw,
  Route,
  Search,
  Star,
  Users,
  X,
  XCircle,
} from "lucide-react";
import config from "../../config";
import "./style.scss";

const backendUrl = config.backend_url;
const defaultCenter = [22.9734, 78.6569];
const statusColors = {
  done: "#16a34a",
  pending: "#f59e0b",
  planned: "#2563eb",
};
const actorPalette = [
  "#2563eb",
  "#dc2626",
  "#16a34a",
  "#9333ea",
  "#f97316",
  "#0891b2",
  "#be123c",
  "#4f46e5",
  "#0f766e",
  "#a16207",
];

const tokenHeaders = () => {
  const token = localStorage.getItem("authToken") || "";
  if (!token) return {};
  return { Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}` };
};

const toDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const parseDateKey = (value) => {
  if (!value) return null;
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const getMonthRange = (date) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return {
    fromDate: toDateKey(start),
    toDate: toDateKey(end),
  };
};

const formatShortDate = (value) => {
  const date = parseDateKey(value);
  if (!date) return "-";
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const formatLongDate = (value) => {
  const date = parseDateKey(value);
  if (!date) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDateKeyFromValue = (value) => {
  if (!value) return "";
  if (String(value).includes("T")) return String(value).slice(0, 10);
  return String(value).slice(0, 10);
};

const numberValue = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const uniqueCount = (rows, key) =>
  new Set(
    (rows || [])
      .map((row) => String(row?.[key] || "").trim())
      .filter(Boolean)
  ).size;

const parseDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatVisitTime = (value) => {
  const date = parseDateTime(value);
  if (!date) return "";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const joinValues = (values) =>
  Array.from(new Set((values || []).map((value) => String(value || "").trim()).filter(Boolean)))
    .sort((a, b) => a.localeCompare(b))
    .join(", ");

const formatGapMinutes = (value) => {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes < 0) return "";
  if (minutes < 60) return `+${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem ? `+${hours}h ${rem}m` : `+${hours}h`;
};

const getCell = (sheet, row, col) => {
  const address = XLSX.utils.encode_cell({ r: row, c: col });
  if (!sheet[address]) sheet[address] = { t: "s", v: "" };
  return sheet[address];
};

const styleRange = (sheet, range, styleFn) => {
  for (let row = range.s.r; row <= range.e.r; row += 1) {
    for (let col = range.s.c; col <= range.e.c; col += 1) {
      const cell = getCell(sheet, row, col);
      cell.s = { ...(cell.s || {}), ...styleFn(row, col, cell) };
    }
  }
};

function FitBounds({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!Array.isArray(points) || points.length === 0) return;
    const bounds = L.latLngBounds(points.map((point) => [point.latitude, point.longitude]));
    if (bounds.isValid()) map.fitBounds(bounds.pad(0.18));
  }, [points, map]);

  return null;
}

function MasterMarketCoverage() {
  const userRole = String(localStorage.getItem("role") || "").toLowerCase();
  const isSuperAdmin = userRole === "super_admin";
  const todayKey = toDateKey(new Date());
  const [fromDate, setFromDate] = useState(todayKey);
  const [toDate, setToDate] = useState(todayKey);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());
  const [search, setSearch] = useState("");
  const [zone, setZone] = useState("");
  const [district, setDistrict] = useState("");
  const [taluka, setTaluka] = useState("");
  const [town, setTown] = useState("");
  const [topDealer, setTopDealer] = useState("all");
  const [status, setStatus] = useState("all");
  const [data, setData] = useState({
    totals: {},
    calendarDays: [],
    actorSummaries: [],
    markers: [],
    rows: [],
    filterOptions: {},
  });
  const [calendarDays, setCalendarDays] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestStatus, setRequestStatus] = useState("all");
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestActionId, setRequestActionId] = useState("");
  const [requestDeskOpen, setRequestDeskOpen] = useState(false);
  const [actorDetail, setActorDetail] = useState(null);
  const [actorDetailSearch, setActorDetailSearch] = useState("");
  const [actorDetailStatus, setActorDetailStatus] = useState("all");
  const [actorCoverageView, setActorCoverageView] = useState("all");
  const [statusActionKey, setStatusActionKey] = useState("");
  const [timelineDownloading, setTimelineDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [approvalSettingLoading, setApprovalSettingLoading] = useState(false);
  const [approvalSettingSaving, setApprovalSettingSaving] = useState(false);

  const calendarDataByDate = useMemo(() => {
    const map = new Map();
    (calendarDays || []).forEach((day) => map.set(day.date, day));
    return map;
  }, [calendarDays]);

  const actorColorMap = useMemo(() => {
    const map = {};
    (data.actorSummaries || []).forEach((actor, index) => {
      map[actor.actorCode] = actorPalette[index % actorPalette.length];
    });
    return map;
  }, [data.actorSummaries]);

  const calendarCells = useMemo(() => {
    const monthStart = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), 1);
    const monthEnd = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 0);
    const firstDayOffset = (monthStart.getDay() + 6) % 7;
    const totalCells = Math.ceil((firstDayOffset + monthEnd.getDate()) / 7) * 7;
    const gridStart = addDays(monthStart, -firstDayOffset);
    const from = parseDateKey(fromDate);
    const to = parseDateKey(toDate || fromDate);

    return Array.from({ length: totalCells }, (_, index) => {
      const date = addDays(gridStart, index);
      const key = toDateKey(date);
      const day = calendarDataByDate.get(key) || {};
      const inRange = from && to && date >= from && date <= to;
      return {
        key,
        label: date.getDate(),
        isCurrentMonth: date.getMonth() === calendarMonth.getMonth(),
        isToday: key === todayKey,
        isRange: inRange,
        total: numberValue(day.total),
        done: numberValue(day.done),
        pending: numberValue(day.pending),
        planned: numberValue(day.planned),
        actors: Array.isArray(day.actors) ? day.actors.length : 0,
        zones: Array.isArray(day.zones) ? day.zones.slice(0, 2) : [],
        towns: Array.isArray(day.towns) ? day.towns.slice(0, 2) : [],
      };
    });
  }, [calendarMonth, calendarDataByDate, fromDate, toDate, todayKey]);

  const markerPoints = useMemo(
    () =>
      (data.markers || [])
        .map((marker) => ({
          ...marker,
          latitude: numberValue(marker.latitude),
          longitude: numberValue(marker.longitude),
        }))
        .filter((marker) => marker.latitude !== 0 && marker.longitude !== 0),
    [data.markers]
  );

  const visibleRequests = useMemo(() => {
    return (requests || []).filter((request) => {
      if (requestStatus !== "all" && request.status !== requestStatus) return false;
      return true;
    });
  }, [requestStatus, requests]);

  const actorDealerRowsByCode = useMemo(() => {
    const map = new Map();
    (data.rows || []).forEach((row) => {
      const code = String(row.actorCode || "").trim();
      if (!code) return;
      if (!map.has(code)) map.set(code, []);
      map.get(code).push(row);
    });
    return map;
  }, [data.rows]);

  const actorDetailRows = useMemo(() => {
    if (!actorDetail?.actorCode) return [];
    const rows = actorDealerRowsByCode.get(actorDetail.actorCode) || [];
    const query = actorDetailSearch.trim().toLowerCase();
    return rows.filter((row) => {
      const rowStatus = String(row.status || "planned").toLowerCase();
      const isTopDealer = Boolean(row.topDealer || row.top_dealer);
      if (actorDetailStatus === "done" && rowStatus !== "done") return false;
      if (actorDetailStatus === "pending" && rowStatus === "done") return false;
      if (actorDetailStatus === "top" && !isTopDealer) return false;
      if (!query) return true;
      return [
        row.date,
        row.actorCode,
        row.actorName,
        row.actorPosition,
        row.dealerCode,
        row.dealerName,
        row.status,
        row.zone,
        row.district,
        row.taluka,
        row.town,
        row.position,
        row.source,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [actorDealerRowsByCode, actorDetail, actorDetailSearch, actorDetailStatus]);

  const timelineRows = useMemo(() => {
    const groups = new Map();

    (data.rows || []).forEach((row, index) => {
      const actorCode = String(row.actorCode || "").trim();
      const dealerCode = String(row.dealerCode || "").trim();
      if (!actorCode || !dealerCode) return;

      const key = `${actorCode}|${dealerCode}`;
      const rowDate = getDateKeyFromValue(row.date);
      const statusValue = String(row.status || "planned").toLowerCase();
      const isDone = statusValue === "done";

      if (!groups.has(key)) {
        groups.set(key, {
          actorCode,
          actorName: row.actorName || actorCode,
          actorPosition: row.actorPosition || row.actorRole || "",
          dealerCode,
          dealerName: row.dealerName || dealerCode,
          topDealer: Boolean(row.topDealer || row.top_dealer),
          latitude: row.latitude,
          longitude: row.longitude,
          mddCode: row.mddCode || "",
          mddName: row.mddName || "",
          category: row.category || "",
          plannedCount: 0,
          visitCount: 0,
          pendingCount: 0,
          dates: new Set(),
          doneDates: new Set(),
          zones: new Set(),
          districts: new Set(),
          talukas: new Set(),
          towns: new Set(),
          firstVisitedAt: null,
          lastVisitedAt: null,
          eventKeys: new Set(),
          doneEventKeys: new Set(),
        });
      }

      const group = groups.get(key);
      group.actorName = group.actorName || row.actorName || actorCode;
      group.actorPosition = group.actorPosition || row.actorPosition || row.actorRole || "";
      group.dealerName = group.dealerName || row.dealerName || dealerCode;
      group.topDealer = group.topDealer || Boolean(row.topDealer || row.top_dealer);
      group.latitude = group.latitude || row.latitude;
      group.longitude = group.longitude || row.longitude;
      group.mddCode = group.mddCode || row.mddCode || "";
      group.mddName = group.mddName || row.mddName || "";
      group.category = group.category || row.category || "";

      [row.zone].filter(Boolean).forEach((value) => group.zones.add(value));
      [row.district].filter(Boolean).forEach((value) => group.districts.add(value));
      [row.taluka].filter(Boolean).forEach((value) => group.talukas.add(value));
      [row.town].filter(Boolean).forEach((value) => group.towns.add(value));
      if (rowDate) group.dates.add(rowDate);

      const eventKey = row.id || `${actorCode}|${rowDate}|${dealerCode}|${index}`;
      if (!group.eventKeys.has(eventKey)) {
        group.eventKeys.add(eventKey);
        group.plannedCount += 1;
      }

      if (isDone) {
        const doneEventKey = row.id || `${eventKey}|done`;
        if (!group.doneEventKeys.has(doneEventKey)) {
          group.doneEventKeys.add(doneEventKey);
          group.visitCount += 1;
          if (rowDate) group.doneDates.add(rowDate);
        }

        const visitedAt =
          parseDateTime(row.markedDoneAt) ||
          parseDateTime(row.markedDoneAtIST) ||
          parseDateTime(row.updatedAt);
        if (visitedAt && (!group.firstVisitedAt || visitedAt < group.firstVisitedAt)) {
          group.firstVisitedAt = visitedAt;
        }
        if (visitedAt && (!group.lastVisitedAt || visitedAt > group.lastVisitedAt)) {
          group.lastVisitedAt = visitedAt;
        }
      }
    });

    return Array.from(groups.values())
      .map((group) => {
        const plannedCount = numberValue(group.plannedCount);
        const visitCount = numberValue(group.visitCount);
        const pendingCount = Math.max(plannedCount - visitCount, 0);
        return {
          ...group,
          pendingCount,
          coveragePct: plannedCount ? Math.round((visitCount / plannedCount) * 100) : 0,
          dates: Array.from(group.dates).sort(),
          doneDates: Array.from(group.doneDates).sort(),
          zones: Array.from(group.zones).sort(),
          districts: Array.from(group.districts).sort(),
          talukas: Array.from(group.talukas).sort(),
          towns: Array.from(group.towns).sort(),
          firstVisitedAtText: formatVisitTime(group.firstVisitedAt),
          lastVisitedAtText: formatVisitTime(group.lastVisitedAt),
        };
      })
      .sort(
        (a, b) =>
          b.visitCount - a.visitCount ||
          b.plannedCount - a.plannedCount ||
          String(a.actorName || "").localeCompare(String(b.actorName || "")) ||
          String(a.dealerName || "").localeCompare(String(b.dealerName || ""))
      );
  }, [data.rows]);

  const timelineSummary = useMemo(
    () => ({
      actors: uniqueCount(timelineRows, "actorCode"),
      dealers: timelineRows.length,
      visitedDealers: timelineRows.filter((row) => row.visitCount > 0).length,
      untouchedDealers: timelineRows.filter((row) => row.visitCount === 0).length,
      visits: timelineRows.reduce((sum, row) => sum + numberValue(row.visitCount), 0),
    }),
    [timelineRows]
  );

  const pendingRequestCount = useMemo(
    () => (requests || []).filter((request) => request.status === "pending").length,
    [requests]
  );

  const completionRate = numberValue(data.totals?.completionRate);

  const getActorRows = (actorCode) => actorDealerRowsByCode.get(actorCode) || [];

  const getActorTopStats = (actorCode) => {
    const topRows = getActorRows(actorCode).filter((row) => row.topDealer || row.top_dealer);
    return {
      total: topRows.length,
      done: topRows.filter((row) => String(row.status || "").toLowerCase() === "done").length,
      pending: topRows.filter((row) => String(row.status || "").toLowerCase() !== "done").length,
    };
  };

  const openActorDetail = (actor) => {
    setActorDetail(actor);
    setActorDetailSearch("");
    setActorDetailStatus(actorCoverageView === "top" ? "top" : "all");
  };

  const fetchCoverage = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${backendUrl}/admin/beat-mapping/master-coverage`, {
        params: {
          fromDate,
          toDate: toDate || fromDate,
          search,
          zone,
          district,
          taluka,
          town,
          topDealer,
          status,
        },
        headers: tokenHeaders(),
      });
      setData({
        totals: res?.data?.totals || {},
        calendarDays: Array.isArray(res?.data?.calendarDays) ? res.data.calendarDays : [],
        actorSummaries: Array.isArray(res?.data?.actorSummaries) ? res.data.actorSummaries : [],
        markers: Array.isArray(res?.data?.markers) ? res.data.markers : [],
        rows: Array.isArray(res?.data?.rows) ? res.data.rows : [],
        filterOptions: res?.data?.filterOptions || {},
      });
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load master market coverage.");
      setData({
        totals: {},
        calendarDays: [],
        actorSummaries: [],
        markers: [],
        rows: [],
        filterOptions: {},
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarOverview = async () => {
    try {
      const monthRange = getMonthRange(calendarMonth);
      const res = await axios.get(`${backendUrl}/admin/beat-mapping/master-coverage`, {
        params: {
          fromDate: monthRange.fromDate,
          toDate: monthRange.toDate,
          search,
          zone,
          district,
          taluka,
          town,
          topDealer,
          status,
        },
        headers: tokenHeaders(),
      });
      setCalendarDays(
        Array.isArray(res?.data?.calendarDays) ? res.data.calendarDays : []
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load calendar overview.");
      setCalendarDays([]);
    }
  };

  const fetchRequests = async () => {
    setRequestLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/beat-plans/requests`, {
        params: {
          startDate: fromDate,
          endDate: toDate || fromDate,
          status: requestStatus === "all" ? "" : requestStatus,
        },
        headers: tokenHeaders(),
      });
      setRequests(Array.isArray(res?.data?.requests) ? res.data.requests : []);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load beat plan requests.");
      setRequests([]);
    } finally {
      setRequestLoading(false);
    }
  };

  const fetchApprovalSetting = async () => {
    setApprovalSettingLoading(true);
    try {
      const res = await axios.get(`${backendUrl}/admin/beat-mapping/settings`, {
        headers: tokenHeaders(),
      });
      setApprovalRequired(
        res?.data?.settings?.routeRequestApprovalRequired !== false
      );
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load route approval setting.");
    } finally {
      setApprovalSettingLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(fetchCoverage, 250);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, search, zone, district, taluka, town, topDealer, status]);

  useEffect(() => {
    const timeout = setTimeout(fetchCalendarOverview, 250);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarMonth, search, zone, district, taluka, town, topDealer, status]);

  useEffect(() => {
    const timeout = setTimeout(fetchRequests, 250);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, requestStatus]);

  useEffect(() => {
    fetchApprovalSetting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setSingleDate = (dateKey) => {
    setFromDate(dateKey);
    setToDate(dateKey);
  };

  const moveCalendarMonth = (direction) => {
    setCalendarMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + direction, 1)
    );
  };

  const resetFilters = () => {
    setSearch("");
    setZone("");
    setDistrict("");
    setTaluka("");
    setTown("");
    setTopDealer("all");
    setStatus("all");
    setFromDate(todayKey);
    setToDate(todayKey);
    setCalendarMonth(new Date());
  };

  const downloadRows = () => {
    const rows = (data.rows || []).map((row) => ({
      Date: row.date,
      "Actor Code": row.actorCode,
      "Actor Name": row.actorName,
      Position: row.actorPosition,
      "Dealer Code": row.dealerCode,
      "Dealer Name": row.dealerName,
      Status: row.status,
      Zone: row.zone,
      District: row.district,
      Taluka: row.taluka,
      Town: row.town,
      "Top Dealer": row.topDealer ? "Yes" : "No",
      Category: row.category,
      "MDD Code": row.mddCode,
      "MDD Name": row.mddName,
      Latitude: row.latitude,
      Longitude: row.longitude,
      "Marked Done At": row.markedDoneAtIST || formatVisitTime(row.markedDoneAt),
      Source: row.source,
    }));

    const sheet = XLSX.utils.json_to_sheet(rows);
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Market Coverage");
    XLSX.writeFile(book, `master-market-coverage-${fromDate}-to-${toDate || fromDate}.xlsx`);
  };

  const downloadTimelineRows = async () => {
    if (!timelineRows.length || timelineDownloading) return;
    setTimelineDownloading(true);

    try {
      const XLSXModule = await import("xlsx-js-style");
      const XLSXStyled = XLSXModule?.default || XLSXModule;
      const dateRangeLabel = `${formatLongDate(fromDate)} - ${formatLongDate(toDate || fromDate)}`;
      const plannedTotal = timelineRows.reduce((sum, row) => sum + numberValue(row.plannedCount), 0);
      const pendingTotal = timelineRows.reduce((sum, row) => sum + numberValue(row.pendingCount), 0);
      const topRows = timelineRows.filter((row) => row.topDealer);
      const topDone = topRows.filter((row) => row.visitCount > 0).length;
      const coveragePct = plannedTotal ? Math.round((timelineSummary.visits / plannedTotal) * 100) : 0;

      const actorOverview = Array.from(
        timelineRows.reduce((map, row) => {
          const key = row.actorCode || "unknown";
          if (!map.has(key)) {
            map.set(key, {
              actorCode: row.actorCode,
              actorName: row.actorName,
              actorPosition: row.actorPosition,
              totalDealers: 0,
              doneDealers: 0,
              pendingDealers: 0,
              plannedEvents: 0,
              totalVisits: 0,
              topDealers: 0,
              topDone: 0,
              topPending: 0,
              zones: new Set(),
              towns: new Set(),
            });
          }
          const actor = map.get(key);
          actor.totalDealers += 1;
          actor.plannedEvents += numberValue(row.plannedCount);
          actor.totalVisits += numberValue(row.visitCount);
          if (row.visitCount > 0) actor.doneDealers += 1;
          else actor.pendingDealers += 1;
          if (row.topDealer) {
            actor.topDealers += 1;
            if (row.visitCount > 0) actor.topDone += 1;
            else actor.topPending += 1;
          }
          (row.zones || []).forEach((value) => actor.zones.add(value));
          (row.towns || []).forEach((value) => actor.towns.add(value));
          return map;
        }, new Map()).values()
      ).sort((a, b) => b.doneDealers - a.doneDealers || b.totalDealers - a.totalDealers);

      const overviewRows = [
        ["Market Coverage Timeline Overview"],
        ["Date Range", dateRangeLabel],
        ["Applied Filters", `Status: ${status}; Top Dealer: ${topDealer}; Zone: ${zone || "All"}; District: ${district || "All"}; Taluka: ${taluka || "All"}; Town: ${town || "All"}; Search: ${search || "None"}`],
        [],
        ["Metric", "Value"],
        ["Actors", timelineSummary.actors],
        ["Grouped Dealer Rows", timelineSummary.dealers],
        ["Visited Dealer Rows", timelineSummary.visitedDealers],
        ["Untouched Dealer Rows", timelineSummary.untouchedDealers],
        ["Planned Appearances", plannedTotal],
        ["Total Visits", timelineSummary.visits],
        ["Pending Appearances", pendingTotal],
        ["Coverage %", `${coveragePct}%`],
        ["Top Dealer Rows", topRows.length],
        ["Top Dealer Done", topDone],
        ["Top Dealer Pending", topRows.length - topDone],
        [],
        [
          "Actor Code",
          "Actor Name",
          "Position",
          "Dealers",
          "Done Dealers",
          "Pending Dealers",
          "Planned",
          "Visits",
          "Coverage %",
          "Top Dealers",
          "Top Done",
          "Top Pending",
          "Zones",
          "Towns",
        ],
        ...actorOverview.map((actor) => [
          actor.actorCode,
          actor.actorName,
          actor.actorPosition,
          actor.totalDealers,
          actor.doneDealers,
          actor.pendingDealers,
          actor.plannedEvents,
          actor.totalVisits,
          actor.plannedEvents ? `${Math.round((actor.totalVisits / actor.plannedEvents) * 100)}%` : "0%",
          actor.topDealers,
          actor.topDone,
          actor.topPending,
          joinValues(Array.from(actor.zones)),
          joinValues(Array.from(actor.towns)),
        ]),
      ];

      const groupedVisitCount = new Map(
        timelineRows.map((row) => [`${row.actorCode}|${row.dealerCode}`, row.visitCount])
      );
      const timelineEvents = [...(data.rows || [])]
        .map((row, index) => ({
          ...row,
          index,
          dateKey: getDateKeyFromValue(row.date),
          statusValue: String(row.status || "planned").toLowerCase() === "done" ? "done" : "pending",
          visitDate: parseDateTime(row.markedDoneAt) || parseDateTime(row.markedDoneAtIST),
        }))
        .sort((a, b) => {
          const actorCompare = String(a.actorName || a.actorCode || "").localeCompare(
            String(b.actorName || b.actorCode || "")
          );
          if (actorCompare) return actorCompare;
          const dateCompare = String(a.dateKey || "").localeCompare(String(b.dateKey || ""));
          if (dateCompare) return dateCompare;
          return (a.visitDate?.getTime?.() || 0) - (b.visitDate?.getTime?.() || 0);
        });

      const actorDayDoneRows = new Map();
      timelineEvents.forEach((row) => {
        if (row.statusValue !== "done") return;
        const key = `${row.actorCode}|${row.dateKey}`;
        if (!actorDayDoneRows.has(key)) actorDayDoneRows.set(key, []);
        actorDayDoneRows.get(key).push(row);
      });
      actorDayDoneRows.forEach((rows) => {
        rows.sort((a, b) => (a.visitDate?.getTime?.() || 0) - (b.visitDate?.getTime?.() || 0));
        rows.forEach((row, index) => {
          row.isFirstVisit = index === 0;
          row.isLastVisit = index === rows.length - 1;
          const previous = index > 0 ? rows[index - 1] : null;
          row.gapFromPrevMinutes =
            previous && row.visitDate && previous.visitDate
              ? Math.max(0, Math.round((row.visitDate.getTime() - previous.visitDate.getTime()) / 60000))
              : null;
        });
      });

      const timelineSheetRows = [
        [
          "Date",
          "Day",
          "Actor",
          "Actor Code",
          "Dealer",
          "Dealer Code",
          "Status",
          "Visit Time",
          "Visit Count",
          "Badges",
          "Gap From Previous",
          "MDD",
          "Category",
          "Town",
          "Taluka",
          "District",
          "Zone",
          "Top Dealer",
          "Distance",
          "Latitude",
          "Longitude",
        ],
        ...timelineEvents.map((row) => {
          const badges = [
            row.isFirstVisit ? "FIRST" : "",
            row.isLastVisit ? "LAST" : "",
            row.topDealer || row.top_dealer ? "TOP" : "",
          ].filter(Boolean);
          const visitCount = groupedVisitCount.get(`${row.actorCode}|${row.dealerCode}`) || 0;
          return [
            formatLongDate(row.dateKey),
            parseDateKey(row.dateKey)?.toLocaleDateString("en-IN", { weekday: "short" }) || "",
            row.actorName || row.actorCode,
            row.actorCode,
            row.dealerName || row.dealerCode,
            row.dealerCode,
            row.statusValue === "done" ? "Done" : "Pending / Planned",
            row.statusValue === "done" ? formatVisitTime(row.visitDate || row.markedDoneAtIST) : "",
            visitCount,
            badges.join(", "),
            formatGapMinutes(row.gapFromPrevMinutes),
            [row.mddCode, row.mddName].filter(Boolean).join(" "),
            row.category || "",
            row.town || "",
            row.taluka || "",
            row.district || "",
            row.zone || "",
            row.topDealer || row.top_dealer ? "Yes" : "No",
            row.distance || "",
            row.latitude || "",
            row.longitude || "",
          ];
        }),
      ];

      const overviewSheet = XLSXStyled.utils.aoa_to_sheet(overviewRows);
      const timelineSheet = XLSXStyled.utils.aoa_to_sheet(timelineSheetRows);
      const overviewRange = XLSXStyled.utils.decode_range(overviewSheet["!ref"]);
      const timelineRange = XLSXStyled.utils.decode_range(timelineSheet["!ref"]);

      const titleStyle = {
        font: { bold: true, sz: 16, color: { rgb: "0F172A" } },
        fill: { patternType: "solid", fgColor: { rgb: "EAF0FF" } },
        alignment: { horizontal: "left", vertical: "center" },
      };
      const sectionHeaderStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { patternType: "solid", fgColor: { rgb: "0F172A" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "CBD5E1" } },
          bottom: { style: "thin", color: { rgb: "CBD5E1" } },
          left: { style: "thin", color: { rgb: "CBD5E1" } },
          right: { style: "thin", color: { rgb: "CBD5E1" } },
        },
      };
      const baseCellStyle = {
        alignment: { vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "E2E8F0" } },
          bottom: { style: "thin", color: { rgb: "E2E8F0" } },
          left: { style: "thin", color: { rgb: "E2E8F0" } },
          right: { style: "thin", color: { rgb: "E2E8F0" } },
        },
      };

      styleRange(overviewSheet, overviewRange, (row, col) => {
        if (row === 0) return titleStyle;
        if (row === 4 || row === 17) return sectionHeaderStyle;
        const metricRows = row >= 5 && row <= 15;
        if (metricRows && col === 0) {
          return { ...baseCellStyle, font: { bold: true, color: { rgb: "334155" } }, fill: { patternType: "solid", fgColor: { rgb: "F8FAFC" } } };
        }
        if (row > 17 && [4, 10].includes(col)) {
          return { ...baseCellStyle, fill: { patternType: "solid", fgColor: { rgb: "ECFDF5" } }, font: { color: { rgb: "15803D" }, bold: true } };
        }
        if (row > 17 && [5, 11].includes(col)) {
          return { ...baseCellStyle, fill: { patternType: "solid", fgColor: { rgb: "FFF7ED" } }, font: { color: { rgb: "C2410C" }, bold: true } };
        }
        return baseCellStyle;
      });

      styleRange(timelineSheet, timelineRange, (row, col, cell) => {
        if (row === 0) return sectionHeaderStyle;
        const statusCell = timelineSheet[XLSXStyled.utils.encode_cell({ r: row, c: 6 })];
        const statusText = String(statusCell?.v || "").toLowerCase();
        const isDone = statusText === "done";
        const isTopCol = col === 17 && String(cell?.v || "").toLowerCase() === "yes";
        if (col === 6) {
          return {
            ...baseCellStyle,
            fill: { patternType: "solid", fgColor: { rgb: isDone ? "DDF2E5" : "FFEBEF" } },
            font: { color: { rgb: isDone ? "15803D" : "BE123C" }, bold: true },
          };
        }
        if (isTopCol) {
          return {
            ...baseCellStyle,
            fill: { patternType: "solid", fgColor: { rgb: "FFF2CC" } },
            font: { color: { rgb: "A16207" }, bold: true },
          };
        }
        return {
          ...baseCellStyle,
          fill: { patternType: "solid", fgColor: { rgb: isDone ? "F3FBF6" : "FFF7F8" } },
        };
      });

      overviewSheet["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }];
      overviewSheet["!cols"] = [
        { wch: 18 },
        { wch: 28 },
        { wch: 16 },
        { wch: 12 },
        { wch: 13 },
        { wch: 15 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
        { wch: 13 },
        { wch: 30 },
        { wch: 34 },
      ];
      timelineSheet["!cols"] = [
        { wch: 13 },
        { wch: 8 },
        { wch: 24 },
        { wch: 14 },
        { wch: 30 },
        { wch: 14 },
        { wch: 16 },
        { wch: 18 },
        { wch: 11 },
        { wch: 16 },
        { wch: 16 },
        { wch: 30 },
        { wch: 14 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 16 },
        { wch: 11 },
        { wch: 12 },
        { wch: 12 },
        { wch: 12 },
      ];
      timelineSheet["!autofilter"] = { ref: timelineSheet["!ref"] };
      timelineSheet["!freeze"] = { xSplit: 0, ySplit: 1 };
      overviewSheet["!freeze"] = { xSplit: 0, ySplit: 18 };

      const book = XLSXStyled.utils.book_new();
      XLSXStyled.utils.book_append_sheet(book, overviewSheet, "Overview");
      XLSXStyled.utils.book_append_sheet(book, timelineSheet, "Timeline");
      XLSXStyled.writeFile(book, `market-coverage-timeline-${fromDate}-to-${toDate || fromDate}.xlsx`);
    } finally {
      setTimelineDownloading(false);
    }
  };

  const reviewRequest = async (requestId, action) => {
    setRequestActionId(`${requestId}:${action}`);
    setError("");
    try {
      await axios.put(
        `${backendUrl}/admin/beat-plans/requests/${requestId}/${action}`,
        {},
        { headers: tokenHeaders() }
      );
      await Promise.all([fetchRequests(), fetchCoverage()]);
    } catch (err) {
      setError(err?.response?.data?.message || `Failed to ${action} request.`);
    } finally {
      setRequestActionId("");
    }
  };

  const updateApprovalSetting = async () => {
    if (approvalSettingSaving || approvalSettingLoading) return;

    const nextValue = !approvalRequired;
    const confirmed = window.confirm(
      nextValue
        ? "Turn approval requirement ON? New route requests will wait for admin approval."
        : "Turn approval requirement OFF? New route requests will be auto-approved as soon as users submit them."
    );
    if (!confirmed) return;

    setApprovalSettingSaving(true);
    setError("");
    try {
      const res = await axios.put(
        `${backendUrl}/admin/beat-mapping/settings`,
        { routeRequestApprovalRequired: nextValue },
        { headers: tokenHeaders() }
      );
      setApprovalRequired(
        res?.data?.settings?.routeRequestApprovalRequired !== false
      );
      await fetchRequests();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update route approval setting.");
    } finally {
      setApprovalSettingSaving(false);
    }
  };

  const updateEmployeeScheduleStatus = async (row, nextStatus) => {
    const normalizedNextStatus = String(nextStatus || "").toLowerCase();
    const currentStatus = String(row.status || "planned").toLowerCase();
    const currentComparableStatus = currentStatus === "done" ? "done" : "pending";
    if (!isSuperAdmin || !["done", "pending"].includes(normalizedNextStatus)) return;
    if (currentComparableStatus === normalizedNextStatus) return;

    const actionKey = row.id || `${row.actorCode}-${row.dealerCode}-${row.date}`;
    setStatusActionKey(actionKey);
    setError("");

    try {
      await axios.post(
        `${backendUrl}/super-admin/update-weekly-beat-status`,
        {
          scheduleId: row.scheduleId || "",
          entryId: row.entryId || "",
          actorCode: row.actorCode,
          dealerCode: row.dealerCode,
          date: getDateKeyFromValue(row.date),
          status: normalizedNextStatus,
        },
        { headers: tokenHeaders() }
      );
      await Promise.all([fetchCoverage(), fetchCalendarOverview()]);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to update dealer status.");
    } finally {
      setStatusActionKey("");
    }
  };

  const refreshAll = () => {
    fetchCoverage();
    fetchCalendarOverview();
    fetchRequests();
    fetchApprovalSetting();
  };

  const monthLabel = calendarMonth.toLocaleDateString("en-IN", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="master-coverage-page">
      <section className="mc-hero">
        <div>
          <span>Field Ops Command</span>
          <h1>Master Market Coverage</h1>
          <p>
            Calendar plans, live beat status, coverage geography and top dealer visibility in one
            place.
          </p>
        </div>
        <div className="mc-hero-actions">
          <button
            type="button"
            className={`mc-approval-toggle ${approvalRequired ? "required" : "auto"}`}
            onClick={updateApprovalSetting}
            disabled={approvalSettingLoading || approvalSettingSaving}
          >
            {approvalSettingSaving || approvalSettingLoading ? (
              <Loader2 size={16} className="spin" />
            ) : approvalRequired ? (
              <CheckCircle2 size={16} />
            ) : (
              <XCircle size={16} />
            )}
            {approvalRequired ? "Approval Required" : "Auto Approve"}
          </button>
          <button type="button" onClick={refreshAll} disabled={loading || requestLoading}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button type="button" onClick={downloadRows} disabled={!data.rows?.length}>
            <Download size={16} />
            Download
          </button>
          <button
            type="button"
            onClick={downloadTimelineRows}
            disabled={!timelineRows.length || timelineDownloading}
          >
            {timelineDownloading ? <Loader2 size={16} className="spin" /> : <ClipboardList size={16} />}
            {timelineDownloading ? "Preparing..." : "Timeline XLSX"}
          </button>
        </div>
      </section>

      <section className="mc-kpis">
        <div className="mc-kpi">
          <Users size={18} />
          <span>Actors</span>
          <strong>{numberValue(data.totals?.actors)}</strong>
        </div>
        <div className="mc-kpi">
          <Route size={18} />
          <span>Planned Visits</span>
          <strong>{numberValue(data.totals?.total)}</strong>
        </div>
        <div className="mc-kpi done">
          <LocateFixed size={18} />
          <span>Done</span>
          <strong>{numberValue(data.totals?.done)}</strong>
        </div>
        <div className="mc-kpi pending">
          <CalendarDays size={18} />
          <span>Pending</span>
          <strong>{numberValue(data.totals?.pending)}</strong>
        </div>
        <div className="mc-kpi star">
          <Star size={18} />
          <span>Top Dealers</span>
          <strong>{numberValue(data.totals?.topDealers)}</strong>
        </div>
      </section>

      <section className="mc-workspace">
        <aside className="mc-filters">
          <div className="mc-panel-title">
            <Filter size={17} />
            <span>Filters</span>
          </div>

          <label>
            Date Range
            <div className="mc-date-pair">
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
          </label>

          <label>
            Search
            <div className="mc-search">
              <Search size={15} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Actor, dealer, town"
              />
            </div>
          </label>

          <label>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="planned">Planned</option>
              <option value="pending">Pending</option>
              <option value="done">Done</option>
            </select>
          </label>

          <label>
            Top Dealer
            <select value={topDealer} onChange={(e) => setTopDealer(e.target.value)}>
              <option value="all">Top Dealer: All</option>
              <option value="yes">Top Dealer: Yes</option>
              <option value="no">Top Dealer: No</option>
            </select>
          </label>

          <label>
            Zone
            <select value={zone} onChange={(e) => setZone(e.target.value)}>
              <option value="">All Zones</option>
              {(data.filterOptions?.zones || []).map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            District
            <select value={district} onChange={(e) => setDistrict(e.target.value)}>
              <option value="">All Districts</option>
              {(data.filterOptions?.districts || []).map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            Taluka
            <select value={taluka} onChange={(e) => setTaluka(e.target.value)}>
              <option value="">All Talukas</option>
              {(data.filterOptions?.talukas || []).map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            Town
            <select value={town} onChange={(e) => setTown(e.target.value)}>
              <option value="">All Towns</option>
              {(data.filterOptions?.towns || []).map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <button type="button" className="mc-reset" onClick={resetFilters}>
            Reset Filters
          </button>
        </aside>

        <main className="mc-main">
          {error ? <div className="mc-error">{error}</div> : null}

          <div className="mc-top-grid">
            <section className="mc-calendar-panel">
              <div className="mc-section-head">
                <div>
                  <span>Plan Calendar</span>
                  <h2>{monthLabel}</h2>
                  <p className="mc-section-subtitle">
                    Month overview stays visible; click a day to inspect its plan.
                  </p>
                </div>
                <div className="mc-calendar-nav">
                  <button type="button" onClick={() => moveCalendarMonth(-1)}>
                    ‹
                  </button>
                  <button type="button" onClick={() => moveCalendarMonth(1)}>
                    ›
                  </button>
                </div>
              </div>

              <div className="mc-weekdays">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>

              <div className="mc-calendar-grid">
                {calendarCells.map((cell) => (
                  <button
                    key={cell.key}
                    type="button"
                    className={[
                      "mc-day",
                      cell.isCurrentMonth ? "" : "muted",
                      cell.isToday ? "today" : "",
                      cell.isRange ? "in-range" : "",
                      cell.total ? "has-plan" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => setSingleDate(cell.key)}
                  >
                    <span>{cell.label}</span>
                    {cell.total ? (
                      <div className="mc-day-metrics">
                        <strong>{cell.total}</strong>
                        <small>{cell.done} done</small>
                      </div>
                    ) : (
                      <small>No plan</small>
                    )}
                    {cell.zones.length || cell.towns.length ? (
                      <em>{[...cell.zones, ...cell.towns].filter(Boolean).join(", ")}</em>
                    ) : null}
                  </button>
                ))}
              </div>
            </section>

            <section className="mc-map-panel">
              <div className="mc-map-header">
                <div>
                  <span>Coverage Map</span>
                  <h2>{markerPoints.length} plotted visits</h2>
                </div>
                <div
                  className="mc-progress"
                  style={{ "--progress": `${Math.min(completionRate, 100) * 3.6}deg` }}
                >
                  <strong>{completionRate}%</strong>
                  <span>complete</span>
                </div>
              </div>

              <MapContainer className="mc-map" center={defaultCenter} zoom={5} scrollWheelZoom>
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds points={markerPoints} />
                {markerPoints.map((point) => {
                  const statusColor = statusColors[point.status] || "#64748b";
                  const actorColor = actorColorMap[point.actorCode] || "#0f172a";
                  return (
                    <CircleMarker
                      key={point.id}
                      center={[point.latitude, point.longitude]}
                      radius={point.topDealer ? 9 : 7}
                      pathOptions={{
                        color: actorColor,
                        weight: 3,
                        fillColor: statusColor,
                        fillOpacity: 0.78,
                      }}
                    >
                      <Popup minWidth={280}>
                        <div className="mc-popup">
                          <strong>{point.dealerName || point.dealerCode}</strong>
                          <span>{point.dealerCode}</span>
                          <div className="mc-popup-status" style={{ backgroundColor: statusColor }}>
                            {point.status}
                          </div>
                          <p>
                            {point.actorName || point.actorCode} · {formatShortDate(point.date)}
                          </p>
                          <p>
                            {[point.zone, point.district, point.taluka, point.town]
                              .filter(Boolean)
                              .join(" / ") || "No geo tags"}
                          </p>
                          {point.topDealer ? <em>Top dealer</em> : null}
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>

              <div className="mc-map-legend">
                <span>
                  <i style={{ backgroundColor: statusColors.done }} /> Done
                </span>
                <span>
                  <i style={{ backgroundColor: statusColors.pending }} /> Pending
                </span>
                <span>
                  <i style={{ backgroundColor: statusColors.planned }} /> Planned
                </span>
              </div>
            </section>
          </div>

          <section className="mc-request-strip">
            <div className="mc-section-head">
              <div>
                <span>Approval Desk</span>
                <h2>Beat Plan Requests</h2>
              </div>
              <button
                type="button"
                className="mc-open-requests"
                onClick={() => setRequestDeskOpen(true)}
              >
                <ClipboardList size={16} />
                Open Request Desk
              </button>
            </div>

            <div className="mc-request-summary">
              <span>
                <CalendarDays size={14} />
                {formatLongDate(fromDate)} - {formatLongDate(toDate || fromDate)}
              </span>
              <span>
                <ClipboardList size={14} />
                {requests.length} total request{requests.length === 1 ? "" : "s"}
              </span>
              <span>
                <CheckCircle2 size={14} />
                {pendingRequestCount} pending approval{pendingRequestCount === 1 ? "" : "s"}
              </span>
              <span>
                <MapPin size={14} />
                Dealer details open in popup
              </span>
            </div>
          </section>

          <section className="mc-timeline-panel">
            <div className="mc-section-head">
              <div>
                <span>Timeline Export</span>
                <h2>Dealer Visit Grouping</h2>
                <p className="mc-section-subtitle">
                  One row per actor and dealer for the selected date range.
                </p>
              </div>
              <button
                type="button"
                className="mc-open-requests"
                onClick={downloadTimelineRows}
                disabled={!timelineRows.length || timelineDownloading}
              >
                {timelineDownloading ? <Loader2 size={16} className="spin" /> : <Download size={16} />}
                {timelineDownloading ? "Preparing Workbook" : "Download Timeline"}
              </button>
            </div>

            <div className="mc-timeline-stats">
              <span>
                <Users size={14} />
                {timelineSummary.actors} actor(s)
              </span>
              <span>
                <MapPin size={14} />
                {timelineSummary.dealers} grouped dealer rows
              </span>
              <span>
                <CheckCircle2 size={14} />
                {timelineSummary.visits} visit(s)
              </span>
              <span>
                <CalendarDays size={14} />
                {timelineSummary.untouchedDealers} untouched
              </span>
            </div>

            <div className="mc-timeline-table">
              <div className="mc-timeline-head">
                <span>Actor</span>
                <span>Dealer</span>
                <span>Visits</span>
                <span>Dates</span>
                <span>Last Visit</span>
                <span>Geo</span>
              </div>
              {timelineRows.length === 0 ? (
                <div className="mc-timeline-empty">No timeline rows for this selection.</div>
              ) : (
                timelineRows.slice(0, 8).map((row) => (
                  <div className="mc-timeline-row" key={`${row.actorCode}-${row.dealerCode}`}>
                    <strong>
                      {row.actorName || row.actorCode}
                      <small>
                        {row.actorCode}
                        {row.actorPosition ? ` · ${row.actorPosition}` : ""}
                      </small>
                    </strong>
                    <strong>
                      {row.dealerName || row.dealerCode}
                      <small>
                        {row.dealerCode}
                        {row.topDealer ? " · Top dealer" : ""}
                      </small>
                    </strong>
                    <span className={row.visitCount ? "done" : "pending"}>
                      {row.visitCount} / {row.plannedCount}
                    </span>
                    <span>{row.dates.map(formatShortDate).slice(0, 4).join(", ") || "-"}</span>
                    <span>{row.lastVisitedAtText || "-"}</span>
                    <span>{joinValues([...row.zones, ...row.towns]) || "-"}</span>
                  </div>
                ))
              )}
            </div>
            {timelineRows.length > 8 ? (
              <p className="mc-timeline-note">
                Showing first 8 grouped rows. Download includes all {timelineRows.length} rows.
              </p>
            ) : null}
          </section>

          {requestDeskOpen ? (
            <div className="mc-request-modal-backdrop" role="presentation">
              <section className="mc-request-modal" role="dialog" aria-modal="true">
                <div className="mc-request-modal-head">
                  <div>
                    <span>Approval Desk</span>
                    <h2>Beat Plan Requests</h2>
                    <p>
                      {formatLongDate(fromDate)} - {formatLongDate(toDate || fromDate)} ·{" "}
                      dealer/MDD-wise request details
                    </p>
                  </div>
                  <button type="button" onClick={() => setRequestDeskOpen(false)}>
                    <X size={19} />
                  </button>
                </div>

                <div className="mc-request-modal-toolbar">
                  <div className="mc-request-summary">
                    <span>
                      <ClipboardList size={14} />
                      {visibleRequests.length} visible request
                      {visibleRequests.length === 1 ? "" : "s"}
                    </span>
                    <span>
                      <MapPin size={14} />
                      Showing requests for the selected date range
                    </span>
                  </div>
                  <div className="mc-request-tools">
                    {requestLoading ? (
                      <span className="mc-inline-loader">
                        <Loader2 size={14} />
                        Loading
                      </span>
                    ) : null}
                    <select
                      value={requestStatus}
                      onChange={(e) => setRequestStatus(e.target.value)}
                    >
                      <option value="all">All requests</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>

                <div className="mc-request-modal-body">
                  <div className="mc-request-list">
                    {visibleRequests.length === 0 ? (
                      <div className="mc-empty">
                        <ClipboardList size={28} />
                        No beat plan requests found for this date/filter selection.
                      </div>
                    ) : (
                      visibleRequests.map((request) => {
                        const dealers = Array.isArray(request.dealers) ? request.dealers : [];
                        const dates = Array.isArray(request.dates)
                          ? request.dates.map(getDateKeyFromValue).filter(Boolean)
                          : [];
                        const actors = Array.isArray(request.targetActors)
                          ? request.targetActors
                          : [];
                        const pending = request.status === "pending";
                        return (
                          <article
                            key={request._id}
                            className={`mc-request-card ${request.status || ""}`}
                          >
                            <div className="mc-request-head">
                              <div>
                                <div className="mc-request-title">
                                  <strong>{request.requestNo || "Beat request"}</strong>
                                  <span>{request.status || "pending"}</span>
                                </div>
                                <p>
                                  Requested by{" "}
                                  {request.requestedBy?.name || request.requestedBy?.code || "-"} ·{" "}
                                  {request.scopeType || "self"} ·{" "}
                                  {formatLongDate(getDateKeyFromValue(request.createdAt))}
                                </p>
                              </div>
                              <div className="mc-request-actions">
                                {pending ? (
                                  <>
                                    <button
                                      type="button"
                                      className="approve"
                                      disabled={Boolean(requestActionId)}
                                      onClick={() => reviewRequest(request._id, "approve")}
                                    >
                                      {requestActionId === `${request._id}:approve` ? (
                                        <Loader2 size={14} />
                                      ) : (
                                        <CheckCircle2 size={15} />
                                      )}
                                      Approve
                                    </button>
                                    <button
                                      type="button"
                                      className="reject"
                                      disabled={Boolean(requestActionId)}
                                      onClick={() => reviewRequest(request._id, "reject")}
                                    >
                                      {requestActionId === `${request._id}:reject` ? (
                                        <Loader2 size={14} />
                                      ) : (
                                        <XCircle size={15} />
                                      )}
                                      Reject
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            </div>

                            <div className="mc-request-date-row">
                              <span className="mc-date-range">
                                {formatLongDate(getDateKeyFromValue(request.startDate))} -{" "}
                                {formatLongDate(getDateKeyFromValue(request.endDate))}
                              </span>
                              <div>
                                {dates.slice(0, 8).map((date) => (
                                  <button
                                    key={date}
                                    type="button"
                                    onClick={() => {
                                      setSingleDate(date);
                                      setRequestDeskOpen(false);
                                    }}
                                  >
                                    {formatShortDate(date)}
                                  </button>
                                ))}
                                {dates.length > 8 ? <em>+{dates.length - 8} more</em> : null}
                              </div>
                            </div>

                            <div className="mc-request-stats">
                              <span>{actors.length} actor(s)</span>
                              <span>{dealers.length} dealer/MDD</span>
                              <span>{uniqueCount(dealers, "town")} town(s)</span>
                              <span>{uniqueCount(dealers, "zone")} zone(s)</span>
                            </div>

                            <div className="mc-request-actors">
                              {actors.map((actor) => (
                                <span key={actor.code || actor.name}>
                                  {actor.name || actor.code}
                                  {actor.position ? ` · ${actor.position}` : ""}
                                </span>
                              ))}
                            </div>

                            <div className="mc-request-dealers">
                              <div className="mc-request-dealer-head">
                                <span>Dealer / MDD</span>
                                <span>Town</span>
                                <span>Taluka</span>
                                <span>District</span>
                                <span>Zone</span>
                              </div>
                              {dealers.slice(0, 12).map((dealer) => (
                                <div className="mc-request-dealer-row" key={dealer.code}>
                                  <strong>
                                    {dealer.name || dealer.code}
                                    <small>
                                      {dealer.code}
                                      {dealer.position ? ` · ${dealer.position}` : ""}
                                    </small>
                                  </strong>
                                  <span>{dealer.town || "-"}</span>
                                  <span>{dealer.taluka || "-"}</span>
                                  <span>{dealer.district || "-"}</span>
                                  <span>
                                    {dealer.zone || "-"}
                                    {dealer.top_dealer || dealer.topDealer ? <b>Top</b> : null}
                                  </span>
                                </div>
                              ))}
                              {dealers.length > 12 ? (
                                <div className="mc-request-more">
                                  +{dealers.length - 12} more dealer/MDD rows
                                </div>
                              ) : null}
                            </div>
                          </article>
                        );
                      })
                    )}
                  </div>
                </div>
              </section>
            </div>
          ) : null}

          <section className="mc-actor-panel">
            <div className="mc-section-head">
              <div>
                <span>People Coverage</span>
                <h2>Actor Plans</h2>
              </div>
              <div className="mc-actor-actions">
                <div className="mc-actor-toggle" aria-label="Actor coverage view">
                  <button
                    type="button"
                    className={actorCoverageView === "all" ? "active" : ""}
                    onClick={() => setActorCoverageView("all")}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={actorCoverageView === "top" ? "active" : ""}
                    onClick={() => setActorCoverageView("top")}
                  >
                    Top Dealers
                  </button>
                </div>
                {loading ? <div className="mc-loading">Loading...</div> : null}
              </div>
            </div>

            <div className="mc-actor-list">
              {(data.actorSummaries || []).length === 0 ? (
                <div className="mc-empty">
                  <MapPinned size={28} />
                  No coverage plans found for this selection.
                </div>
              ) : (
                data.actorSummaries.map((actor) => {
                  const color = actorColorMap[actor.actorCode] || "#0f172a";
                  const topStats = getActorTopStats(actor.actorCode);
                  const cardStats =
                    actorCoverageView === "top"
                      ? topStats
                      : {
                          total: numberValue(actor.total),
                          done: numberValue(actor.done),
                          pending: numberValue(actor.pending),
                        };
                  const donePct = cardStats.total
                    ? Math.round((cardStats.done / cardStats.total) * 100)
                    : 0;
                  return (
                    <article
                      key={actor.actorCode}
                      className={`mc-actor-card ${actorCoverageView === "top" ? "top-view" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => openActorDetail(actor)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          openActorDetail(actor);
                        }
                      }}
                    >
                      <div className="mc-actor-top">
                        <i style={{ backgroundColor: color }} />
                        <div>
                          <strong>{actor.actorName || actor.actorCode}</strong>
                          <span>
                            {actor.actorCode} · {actor.actorPosition || "Field actor"}
                          </span>
                        </div>
                        <div className="mc-actor-score">
                          <b>{donePct}%</b>
                          {actorCoverageView === "top" ? <small>top dealers</small> : null}
                        </div>
                      </div>
                      <div className="mc-actor-bar">
                        <span style={{ width: `${donePct}%`, backgroundColor: color }} />
                      </div>
                      <div className="mc-actor-meta">
                        <span className="total">{cardStats.total} total</span>
                        <span className="done">{cardStats.done} done</span>
                        <span className="pending">{cardStats.pending} pending</span>
                      </div>
                      <p>
                        {[...(actor.zones || []), ...(actor.towns || [])]
                          .slice(0, 4)
                          .join(", ") || "No geography tagged"}
                      </p>
                    </article>
                  );
                })
              )}
            </div>
          </section>
        </main>
      </section>

      {actorDetail ? (
        <div className="mc-employee-modal-backdrop" role="presentation" onClick={() => setActorDetail(null)}>
          <section
            className="mc-employee-modal"
            role="dialog"
            aria-modal="true"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mc-employee-modal-head">
              <div>
                <h2>Employee Schedule</h2>
                <p>
                  {actorDetail.actorName || actorDetail.actorCode} · {actorDetail.actorCode} ·{" "}
                  {formatLongDate(fromDate)} - {formatLongDate(toDate || fromDate)}
                </p>
              </div>
              <button type="button" onClick={() => setActorDetail(null)}>
                <X size={20} />
              </button>
            </div>

            <div className="mc-employee-toolbar">
              <div className="mc-employee-search">
                <Search size={15} />
                <input
                  type="text"
                  value={actorDetailSearch}
                  onChange={(event) => setActorDetailSearch(event.target.value)}
                  placeholder="Search by code, name, district, taluka, zone or position"
                />
              </div>
              <select
                value={actorDetailStatus}
                onChange={(event) => setActorDetailStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="done">Done</option>
                <option value="pending">Pending / Planned</option>
                <option value="top">Top Dealers</option>
              </select>
            </div>

            <div className="mc-employee-grid">
              {actorDetailRows.length === 0 ? (
                <div className="mc-empty">
                  <MapPinned size={28} />
                  No matching dealer/MDD rows for this actor.
                </div>
              ) : (
                actorDetailRows.map((row, index) => {
                  const rowStatus = String(row.status || "planned").toLowerCase();
                  const isDone = rowStatus === "done";
                  const actionKey = row.id || `${row.actorCode}-${row.dealerCode}-${row.date}`;
                  const statusBusy = statusActionKey === actionKey;
                  const statusSelectValue = isDone ? "done" : "pending";
                  const isTopDealer = Boolean(row.topDealer || row.top_dealer);
                  return (
                    <article
                      key={`${row.date}-${row.actorCode}-${row.dealerCode}-${index}`}
                      className={`mc-employee-card ${isDone ? "done" : "pending"}`}
                    >
                      <div className="mc-employee-card-top">
                        <strong>{row.dealerCode || "N/A"}</strong>
                        {isSuperAdmin ? (
                          <div className={`mc-employee-status-edit ${isDone ? "done" : "pending"}`}>
                            {statusBusy ? <Loader2 size={13} className="spin" /> : null}
                            <select
                              value={statusSelectValue}
                              disabled={statusBusy}
                              onChange={(event) => updateEmployeeScheduleStatus(row, event.target.value)}
                            >
                              <option value="done">Done</option>
                              <option value="pending">Pending / Planned</option>
                            </select>
                          </div>
                        ) : (
                          <span className={`mc-employee-status ${isDone ? "done" : "pending"}`}>
                            {isDone ? "Done" : rowStatus || "Pending"}
                          </span>
                        )}
                      </div>
                      <h3>{row.dealerName || row.dealerCode || "Unnamed dealer"}</h3>
                      <div className="mc-employee-tags">
                        {[row.zone, row.district, row.taluka, row.town, row.position || row.dealerPosition]
                          .filter(Boolean)
                          .slice(0, 6)
                          .map((tag) => (
                            <span key={tag}>{tag}</span>
                          ))}
                        {isTopDealer ? <b>Top dealer</b> : null}
                      </div>
                      <div className="mc-employee-card-bottom">
                        <span>
                          <MapPin size={16} />
                          {formatShortDate(getDateKeyFromValue(row.date))}
                        </span>
                        <span>
                          <LocateFixed size={16} />
                          {row.distance ? `${String(row.distance).slice(0, 4)} Km` : "N/A"}
                        </span>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </section>
        </div>
      ) : null}
    </div>
  );
}

export default MasterMarketCoverage;
